import { useState, useMemo, useEffect, useCallback } from 'react';

import { getModalConfig } from '../LocationsContainer/getModalConfig';
import { CompanyData } from '../LocationsContainer/types';
import {
  createDebounceEffect,
  transformCompaniesForModal
} from '../LocationsContainer/utils';
import styles from '../styles.module.css';
import CompanyIcon from '@/assets/icons/company.svg?react';
import {
  AdminCardBody,
  AdminCardHeader,
  AdminTableBody
} from '@/components/AdminToolComponents/card';
import DataTable from '@/components/DataTable';
import Loader from '@/components/Form/Loader';
import PlusButton from '@/components/Form/PlusButton';
import { getCompaniesColumns } from '@/components/LocationDetails/companiesColumns';
import SearchComponent from '@/components/LocationDetails/searchComponent';
import useModal from '@/context/ModalContext/useModal';
import {
  useGetCompaniesInitial,
  useSearchCompanies
} from '@/hooks/services/Companies';

export type OnAssignCompanies = (
  companies: Array<{ id: string; name: string }>,
  options?: { onSuccess?: () => void }
) => Promise<void>;

export type OnUnassignCompany = (companyId: string) => Promise<void>;

interface CompaniesAssignedTableProps {
  companiesForTable: CompanyData[];
  canAssignUnassignCompanies: boolean;
  onAssignCompanies: OnAssignCompanies;
  onUnassignCompany: OnUnassignCompany;
}

export const CompaniesAssignedTable = ({
  companiesForTable,
  canAssignUnassignCompanies = false,
  onAssignCompanies,
  onUnassignCompany
}: CompaniesAssignedTableProps) => {
  const { updateModalContent, closeModal } = useModal();
  const [isCompanyModalOpen, setIsCompanyModalOpen] = useState(false);
  const [companiesSearchTerm, setCompaniesSearchTerm] = useState('');
  const [debouncedCompaniesSearchTerm, setDebouncedCompaniesSearchTerm] =
    useState('');
  const [selectedCompanies, setSelectedCompanies] = useState<
    Array<{ id: string; name: string }>
  >([]);

  const {
    data: dataCompaniesInitial,
    isLoading: isLoadingCompaniesInitial,
    error: errorCompaniesInitial,
    isError: isErrorCompaniesInitial
  } = useGetCompaniesInitial({
    useQueryOptions: {
      enabled: isCompanyModalOpen,
      staleTime: 0,
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false
    }
  });

  const {
    data: dataCompanies,
    isLoading: isLoadingCompanies,
    error: errorCompanies,
    isError: isErrorCompanies
  } = useSearchCompanies(debouncedCompaniesSearchTerm, {
    useQueryOptions: {
      staleTime: Infinity,
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      enabled: isCompanyModalOpen && debouncedCompaniesSearchTerm.length > 0
    }
  });

  useEffect(
    createDebounceEffect(companiesSearchTerm, setDebouncedCompaniesSearchTerm),
    [companiesSearchTerm]
  );

  const companiesForModal = useMemo(() => {
    const companiesToUse =
      debouncedCompaniesSearchTerm.length > 0
        ? dataCompanies?.data || []
        : dataCompaniesInitial?.data || [];
    const isError =
      debouncedCompaniesSearchTerm.length > 0
        ? isErrorCompanies
        : isErrorCompaniesInitial;
    return transformCompaniesForModal(companiesToUse, isError);
  }, [
    dataCompanies,
    isErrorCompanies,
    dataCompaniesInitial,
    isErrorCompaniesInitial,
    debouncedCompaniesSearchTerm
  ]);

  const handleCloseCompanyModal = useCallback(() => {
    closeModal();
    setIsCompanyModalOpen(false);
    setCompaniesSearchTerm('');
    setDebouncedCompaniesSearchTerm('');
    setSelectedCompanies([]);
  }, [closeModal]);

  const handleCompaniesSearchChange = useCallback((value: string) => {
    setCompaniesSearchTerm(value);
  }, []);

  const handleOpenAssignModal = useCallback(() => {
    setCompaniesSearchTerm('');
    setDebouncedCompaniesSearchTerm('');
    setSelectedCompanies([]);
    setIsCompanyModalOpen(true);
  }, []);

  const updateCompanyModalContent = useCallback(() => {
    if (!isCompanyModalOpen) return;

    const config = getModalConfig(
      'company',
      companiesForModal,
      [],
      [],
      handleCloseCompanyModal,
      [],
      undefined,
      selectedCompanies,
      async (companies: Array<{ id: string; name: string }>) =>
        onAssignCompanies(companies, { onSuccess: handleCloseCompanyModal }),
      [],
      undefined
    );

    const createModalContent = () => {
      if (isErrorCompanies || isErrorCompaniesInitial) {
        return (
          <div className={styles.errorContainer}>
            Error loading companies data:{' '}
            {(errorCompanies || errorCompaniesInitial)?.message ||
              'Unknown error'}
          </div>
        );
      }

      const showLoader =
        debouncedCompaniesSearchTerm.length > 0
          ? isLoadingCompanies
          : isLoadingCompaniesInitial;

      return (
        <div className={styles.formModal}>
          <SearchComponent
            icon={config.icon || null}
            title={config.title || ''}
            onSearchChange={handleCompaniesSearchChange}
          />
          <div className={styles.formBody}>
            {showLoader ? (
              <div className={styles.loadingContainer}>
                <Loader size="large" />
              </div>
            ) : (
              <DataTable
                rows={(config.rows as CompanyData[]) || []}
                className={styles.dataTableModal}
                columns={config.columns || []}
                selectable={true}
                selectedRows={selectedCompanies as any}
                onRowSelectionChange={setSelectedCompanies as any}
                getRowId={row => row.id}
                initialState={{
                  sorting: {
                    field: 'name',
                    sort: 'asc'
                  }
                }}
              />
            )}
          </div>
        </div>
      );
    };

    updateModalContent(createModalContent(), {
      modalButtons: config.modalButtons
    });
  }, [
    isCompanyModalOpen,
    isErrorCompanies,
    isErrorCompaniesInitial,
    errorCompanies,
    errorCompaniesInitial,
    companiesForModal,
    debouncedCompaniesSearchTerm.length,
    isLoadingCompanies,
    isLoadingCompaniesInitial,
    selectedCompanies,
    handleCloseCompanyModal,
    handleCompaniesSearchChange,
    onAssignCompanies,
    updateModalContent
  ]);

  useEffect(() => {
    if (isCompanyModalOpen) {
      updateCompanyModalContent();
    }
  }, [isCompanyModalOpen, updateCompanyModalContent]);

  const handleUnassignClick = useCallback(
    (company: { id: string; name: string }) => {
      updateModalContent(null, {
        modalTitle: 'Unassign Company',
        modalIcon: CompanyIcon,
        className: styles.unassignModalContainer,
        modalButtons: [
          {
            label: 'CANCEL',
            onClick: () => {
              closeModal();
            },
            variant: 'cancelBlue'
          },
          {
            label: 'UNASSIGN COMPANY',
            onClick: async () => {
              try {
                await onUnassignCompany(company.id);
              } catch (error) {
                console.error('Error in unassign company:', error);
              }
            },
            variant: 'okBlue',
            isLoader: true
          }
        ],
        alignButtons: 'center',
        contentTitle: 'You are about to unassign',
        contentSubtitle: `Company ${company.name}`,
        contentDescription: (
          <div className={styles.contentDescription}>
            <span>Are you sure you want to proceed?</span>
          </div>
        )
      });
    },
    [updateModalContent, closeModal, onUnassignCompany]
  );

  return (
    <div className={styles.adminContainer}>
      <AdminCardHeader
        left={
          <>
            <CompanyIcon className={styles.adminIconHeader} />
            <h2 className={styles.adminTitle}>Companies</h2>
          </>
        }
        right={
          canAssignUnassignCompanies ? (
            <PlusButton
              onClick={handleOpenAssignModal}
              label="Assign a company"
            />
          ) : undefined
        }
      />

      {companiesForTable.length > 0 ? (
        <AdminTableBody>
          <DataTable
            rows={companiesForTable}
            className={styles.dataTable}
            columns={getCompaniesColumns(
              canAssignUnassignCompanies,
              handleUnassignClick
            )}
            initialState={{
              sorting: {
                field: 'name',
                sort: 'asc'
              }
            }}
          />
        </AdminTableBody>
      ) : (
        <AdminCardBody>
          <div className={styles.adminContainerCenteredRow}>
            <h4 className={styles.adminText}>
              No companies have been assigned yet
            </h4>
          </div>
        </AdminCardBody>
      )}
    </div>
  );
};
